import { useLocation, useNavigate } from "react-router-dom";
import { SubSection } from "../SubSection";
import { NavItem } from "../NavItem";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import MergeOutlinedIcon from "@mui/icons-material/MergeOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/lib/supabase";

interface SettingsSectionProps {
  isCollapsed: boolean;
  /** Holds ADMIN_MANAGE — resolved once in the Sidebar via useCapabilities (anti-flash, single source). */
  isAdmin: boolean;
}

export function SettingsSection({ isCollapsed, isAdmin }: SettingsSectionProps) {
  const location = useLocation();
  const currentPath = location.pathname;
  const navigate = useNavigate();

  const handleConfirmLogout = async () => {
    try {
      await supabase.auth.signOut();
      // Nothing writes these profile copies any more; a browser that still holds one drops it here.
      localStorage.removeItem("userProfile");
      localStorage.removeItem("userAvatar");
      localStorage.removeItem("profile_header");
      localStorage.removeItem("user_email");
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <AlertDialog>
      <SubSection
        title="Tools"
        icon={SettingsOutlinedIcon}
        isCollapsed={isCollapsed}
      >
        <NavItem
          icon={PersonOutlineOutlinedIcon}
          label="Profile"
          path="/profile"
          isCollapsed={isCollapsed}
          isActive={currentPath.includes("/profile")}
        />
        {isAdmin && (
          <>
            <NavItem
              icon={DashboardOutlinedIcon}
              label="Dashboard"
              path="/admin/dashboard"
              isCollapsed={isCollapsed}
              isActive={currentPath === "/admin/dashboard"}
            />
            {/* First among the admin tools because it is the only one with people waiting on it:
                a licensee cannot use anything until someone here decides. */}
            <NavItem
              icon={HowToRegOutlinedIcon}
              label="Approvals"
              path="/admin/approvals"
              isCollapsed={isCollapsed}
              isActive={currentPath === "/admin/approvals"}
            />
            <NavItem
              icon={BusinessOutlinedIcon}
              label="Labels"
              path="/admin/labels"
              isCollapsed={isCollapsed}
              isActive={currentPath === "/admin/labels"}
            />
            {/* Next to Labels because it is the same partners, but the rate is set on the
                payee ORGANIZATION — so this screen also reaches publishers and distributors,
                which have no row in `labels` and are paid on every deal's publishing lane. */}
            <NavItem
              icon={PercentOutlinedIcon}
              label="Commission"
              path="/admin/commissions"
              isCollapsed={isCollapsed}
              isActive={currentPath === "/admin/commissions"}
            />
            {/* Money paid by buyers that has not reached a holder, and buyers charged twice. */}
            <NavItem
              icon={PaymentsOutlinedIcon}
              label="Payouts"
              path="/admin/payouts"
              isCollapsed={isCollapsed}
              isActive={currentPath === "/admin/payouts"}
            />
            {/* Tax both ways: what buyers are charged, what is withheld from holders. */}
            <NavItem
              icon={AccountBalanceOutlinedIcon}
              label="Tax"
              path="/admin/tax"
              isCollapsed={isCollapsed}
              isActive={currentPath === "/admin/tax"}
            />
            <NavItem
              icon={VerifiedUserOutlinedIcon}
              label="NDAs"
              path="/admin/ndas"
              isCollapsed={isCollapsed}
              isActive={currentPath === "/admin/ndas"}
            />
            <NavItem
              icon={MergeOutlinedIcon}
              label="Party Merge Queue"
              path="/admin/parties"
              isCollapsed={isCollapsed}
              isActive={currentPath === "/admin/parties"}
            />
            {/* The merge queue's mirror image, so it sits next to it: merge says "these two rows
                are ONE company", corporate hierarchy says "these two companies are DIFFERENT and
                one owns the other". Confusing the two merges a subsidiary out of existence. */}
            <NavItem
              icon={AccountTreeOutlinedIcon}
              label="Corporate Hierarchy"
              path="/admin/corporate-hierarchy"
              isCollapsed={isCollapsed}
              isActive={currentPath === "/admin/corporate-hierarchy"}
            />
            {/* Sits beside the merge queue because both are staff adjudications over the same
                identity registry: merge decides "these two rows are one company", rights review
                decides "this company is that account" and "this holder's refusal stands". */}
            <NavItem
              icon={GavelOutlinedIcon}
              label="Rights Review"
              path="/admin/rights-review"
              isCollapsed={isCollapsed}
              isActive={currentPath === "/admin/rights-review"}
            />
            <NavItem
              icon={PublicOutlinedIcon}
              label="Publisher World"
              path="/admin/publisher-map"
              isCollapsed={isCollapsed}
              isActive={currentPath === "/admin/publisher-map"}
            />
          </>
        )}
        <NavItem
          icon={SettingsOutlinedIcon}
          label="Settings"
          path="/settings"
          isCollapsed={isCollapsed}
          isActive={currentPath.includes("/settings")}
        />

        <AlertDialogTrigger asChild>
          <button
            type="button"
            className={`group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ease-out hover:bg-accent hover:text-accent-foreground hover:shadow-sm ${
              isCollapsed ? "justify-center" : "justify-start"
            } ${!isCollapsed ? "hover:translate-x-1" : ""} text-muted-foreground`}
          >
            <div
              className={`flex items-center ${
                isCollapsed ? "justify-center" : "gap-3"
              }`}
            >
              <LogoutOutlinedIcon
                fontSize="small"
                className="transition-all duration-200"
              />
              {!isCollapsed && (
                <span className="transition-all duration-200 truncate">
                  Logout
                </span>
              )}
            </div>
          </button>
        </AlertDialogTrigger>
      </SubSection>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Log out?</AlertDialogTitle>
          <AlertDialogDescription>
            You will be signed out of ZenMusic on this device.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirmLogout}>Log out</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
